import React, { useEffect, useState } from "react";
import { GetMoreContactsModal } from "./modal";
import {
  useGetAccountTitleGapsQuery,
  campaignsApi,
  useGetCampaignDetailsQuery,
} from "../../api";
import { JobTitle } from "../../types/apiTypes";
import { toast } from "react-toastify";
import { CustomToast } from "./CustomToast";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "hooks/useReduxHooks";
import { AnalysisStatus, SSEEvents } from "../../types/companyTypes";
import { setIsNotificationsOpen, setStatus } from "../../features/companySlice";
import { GradientButton } from "./GradientButton";
import { KeyRoles } from "../../types/enums";

export const GetMoreContactsForAccountBtn = ({
  role,
  account_id,
}: {
  account_id: string;
  role?: KeyRoles;
}) => {
  const dispatch = useAppDispatch();

  const { analysis_status, scraping_status, scraping_update_status } =
    useAppSelector((state) => state.company.status);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [fetchData, setFetchData] = useState<boolean>(false);
  const [titles, setTitles] = useState<JobTitle[]>([]);

  const { data: campaignDetails } = useGetCampaignDetailsQuery({
    account_id,
  });

  const {
    data,
    error,
    isLoading: isTitleGapsLoading,
  } = useGetAccountTitleGapsQuery(
    { account_id, role },
    {
      skip: !fetchData,
      refetchOnMountOrArgChange: true,
    },
  );

  const [currentStatus, setCurrentStatus] = useState<string>(
    campaignDetails?.status!,
  );
  const [currentScrapingStatus, setCurrentScrapingStatus] = useState<string>(
    campaignDetails?.scraping_status!,
  );
  const [currentScrapingUpdateStatus, setCurrentScrapingUpdateStatus] =
    useState<string>(campaignDetails?.scraping_update_status!);
  const [currentProgress, setCurrentProgress] = useState<number>(
    campaignDetails?.progress!,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAnalyses, setIsAnalyses] = useState<boolean>(false);

  useEffect(() => {
    campaignDetails?.status && setCurrentStatus(campaignDetails.status);
  }, [campaignDetails?.status]);

  useEffect(() => {
    campaignDetails?.scraping_status &&
      setCurrentScrapingStatus(campaignDetails.scraping_status);
  }, [campaignDetails?.scraping_status]);

  useEffect(() => {
    campaignDetails?.scraping_update_status &&
      setCurrentScrapingUpdateStatus(campaignDetails.scraping_update_status);
  }, [campaignDetails?.scraping_update_status]);

  useEffect(() => {
    campaignDetails?.progress && setCurrentProgress(campaignDetails.progress);
  }, [campaignDetails?.progress]);

  useEffect(() => {
    if (scraping_status && scraping_status?.account_id === account_id) {
      setCurrentScrapingStatus(scraping_status.status);
    }
  }, [scraping_status]);

  useEffect(() => {
    if (
      scraping_update_status &&
      scraping_update_status?.account_id === account_id
    ) {
      setCurrentScrapingUpdateStatus(scraping_update_status.status);
    }
  }, [scraping_update_status]);

  useEffect(() => {
    if (analysis_status && analysis_status?.account_id === account_id) {
      setCurrentStatus(analysis_status.status);
      setCurrentProgress(analysis_status.progress);
    }
  }, [analysis_status]);

  const refetchHandler = () => {
    dispatch(
      campaignsApi.util.invalidateTags([
        "members",
        "role-members",
        "company",
        "activities",
      ]),
    );
  };

  useEffect(() => {
    if (
      [AnalysisStatus.queued, AnalysisStatus.in_progress].some(
        (status) => status === currentScrapingStatus,
      )
    )
      setIsLoading(true);

    if (
      AnalysisStatus.completed === currentScrapingStatus &&
      AnalysisStatus.in_progress === currentStatus
    ) {
      setIsAnalyses(true);
    }

    if (AnalysisStatus.completed_no_data === currentScrapingStatus) {
      setIsLoading(false);
      setIsAnalyses(false);
      dispatch(setStatus({ [SSEEvents.scraping_status]: undefined }));
    }

    if (
      [AnalysisStatus.ready, AnalysisStatus.failed].some(
        (status) => status === currentScrapingStatus,
      ) ||
      (AnalysisStatus.completed === currentScrapingStatus &&
        AnalysisStatus.completed === currentStatus)
    ) {
      setIsLoading(false);
      setIsAnalyses(false);
      dispatch(
        setStatus({
          [SSEEvents.scraping_status]: undefined,
          [SSEEvents.analysis_status]: undefined,
        }),
      );
    }
  }, [currentScrapingStatus, currentStatus]);

  useEffect(() => {
    return () => {
      dispatch(
        setStatus({
          [SSEEvents.scraping_status]: undefined,
          [SSEEvents.analysis_status]: undefined,
        }),
      );
    };
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(
        <CustomToast
          type="error"
          title="Failed to find more contacts."
          text={
            getErrorMessage(error).globalError ||
            "Something went wrong. Please try again"
          }
        />,
      );
      setFetchData(false);
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      setTitles(
        data.title_gaps.map((el) => ({
          is_critical: el.is_critical,
          title: el.name,
          id: el.name,
          is_edit: false,
        })),
      );
      setFetchData(false);
      setIsOpen(true);
    }
  }, [data]);

  const onSuccessHandler = () => {
    setIsLoading(true);
    refetchHandler();
    dispatch(setIsNotificationsOpen(true));
  };

  const isUpdateScraping = [
    AnalysisStatus.in_progress,
    AnalysisStatus.queued,
  ].some((status) => status === currentScrapingUpdateStatus);

  const isAnalyzing = [AnalysisStatus.in_progress, AnalysisStatus.queued].some(
    (status) => status === currentStatus,
  );

  const onOpenHandler = () => {
    if (data) {
      return setIsOpen(true);
    }
    setFetchData(true);
  };

  return (
    <>
      <GradientButton
        disabled={
          isLoading ||
          isAnalyses ||
          isUpdateScraping ||
          isAnalyzing ||
          isTitleGapsLoading
        }
        onClick={onOpenHandler}
      >
        <>
          {(isLoading || isAnalyses || isTitleGapsLoading) && (
            <CircularProgress size={20} color="inherit" />
          )}
          {isAnalyses
            ? `Analysis in Progress: ${currentProgress} %`
            : isLoading || isAnalyses
              ? "Search in Progress"
              : "Explore Audience Expansion"}
        </>
      </GradientButton>
      {isOpen && (
        <GetMoreContactsModal
          role={role}
          onSuccess={onSuccessHandler}
          account_id={account_id!}
          initTitles={titles}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          withSuggestions={true}
        />
      )}
    </>
  );
};
